import { ConfigurationGroupType, TimeSlotType } from 'shared-types/SpaPackage';

const isUnlimitedQuantity = (
  quantity: TimeSlotType['quantity'] | undefined
): quantity is null | undefined => quantity === null || quantity === undefined;

const getEquivalenceKey = ({
  startTime,
  endTime,
  durationMinutes,
  price,
  discount,
}: TimeSlotType) =>
  [
    startTime,
    endTime,
    durationMinutes,
    price?.amount,
    price?.currencyCode,
    discount ? JSON.stringify(discount) : '',
  ].join('-');

const mergeUniqueResources = (
  current: string[] | undefined,
  additional: string[] | undefined
): string[] | undefined => {
  if (!current && !additional) {
    return undefined;
  }
  return Array.from(new Set([...(current || []), ...(additional || [])]));
};

export const mergeEquivalentTimeslots = (timeslots: TimeSlotType[] | undefined): TimeSlotType[] => {
  const mergedTimeslots = new Map<string, TimeSlotType>();

  (Array.isArray(timeslots) ? timeslots : []).forEach((timeslot) => {
    if (!timeslot?.startTime) {
      return;
    }

    const key = getEquivalenceKey(timeslot);
    const existingTimeslot = mergedTimeslots.get(key);

    if (!existingTimeslot) {
      mergedTimeslots.set(key, { ...timeslot });
      return;
    }

    mergedTimeslots.set(key, {
      ...existingTimeslot,
      quantity:
        isUnlimitedQuantity(existingTimeslot.quantity) || isUnlimitedQuantity(timeslot.quantity)
          ? null
          : existingTimeslot.quantity + timeslot.quantity,
      staff: mergeUniqueResources(existingTimeslot.staff, timeslot.staff),
      rooms: mergeUniqueResources(existingTimeslot.rooms, timeslot.rooms),
    });
  });

  return Array.from(mergedTimeslots.values());
};

export const canGuestsShareTimeslot = (
  timeslots: TimeSlotType[] | undefined,
  numberOfGuests: number
): boolean =>
  mergeEquivalentTimeslots(timeslots).some(
    ({ quantity }) => isUnlimitedQuantity(quantity) || quantity >= numberOfGuests
  );

export const getOptionTimeslots = (
  configurationGroups: ConfigurationGroupType[] | undefined,
  experienceOptionId: number
): TimeSlotType[] =>
  (Array.isArray(configurationGroups) ? configurationGroups : [])
    .flatMap((group) => (Array.isArray(group?.options) ? group.options : []))
    .filter((option) => Number(option?.id) === Number(experienceOptionId))
    .flatMap((option) => (Array.isArray(option?.timeslots) ? option.timeslots : []));
